/**
 * CampaignsCard — campaign-performance list for the OEM Home strip (Phase 2).
 *
 * Ports the reference HomeOEM "Campaigns" card: a list of campaigns, each with
 * its response count and a responded/sent progress bar. Bars use a magenta ramp
 * anchored on Crop Circle label.magenta — the top responder gets the darkest
 * stop (ranked by response count). OEM-wide — NOT map-plottable. Consumes
 * `useHomeCards(range)`.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Colors, tokens, Skeleton, EmptyState, ErrorState } from '@gripp/shared-ui';
import { DashboardCardShell } from './dashboardCardShell';
import { ProgressBar } from './cardPrimitives';
import { useHomeCards } from '../hooks/useHomeCards';
import { HomeRangeProps } from '../types';

// Magenta ramp by rank — Crop Circle label.magenta at decreasing opacity,
// applied via hex-alpha suffix so the ramp stays tied to the token. Darkest =
// top responder. Clamps to the lightest stop. Suffixes ≈ [1, .78, .6, .46, .34].
const MAGENTA_ALPHAS = ['FF', 'C7', '99', '75', '57'];
const magentaForRank = (rank: number): string => {
  const a = MAGENTA_ALPHAS[Math.min(rank, MAGENTA_ALPHAS.length - 1)];
  return `${tokens.colors.label.magenta}${a}`;
};

export const CampaignsCard: React.FC<HomeRangeProps> = ({ range }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useHomeCards(range);
  const campaigns = data?.campaigns;

  const title = t('home.campaigns.title', 'Campaigns');
  // Mock-backed in this POC — flag the card as illustrative.
  const action = (
    <Text style={styles.sampleTag} testID="campaigns-card-sample">
      {t('home.campaigns.sample', 'Sample data')}
    </Text>
  );

  if (isLoading) {
    return (
      <DashboardCardShell title={title} action={action} testID="campaigns-card"
        loadingSlot={
          <View style={styles.loading}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={28} radius={4} />)}
          </View>
        }
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (error) {
    return (
      <DashboardCardShell title={title} action={action} testID="campaigns-card"
        errorSlot={<ErrorState title={t('home.cards.error', 'Could not load data')} testID="campaigns-card-error" />}
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (!campaigns || campaigns.items.length === 0) {
    return (
      <DashboardCardShell title={title} action={action} testID="campaigns-card"
        emptySlot={<EmptyState title={t('home.campaigns.empty', 'No campaigns')} testID="campaigns-card-empty" />}
      >
        {null}
      </DashboardCardShell>
    );
  }

  // Rank by response count so the strongest responder gets the darkest stop.
  const rankByName = new Map(
    [...campaigns.items]
      .sort((a, b) => b.responded - a.responded)
      .map((c, i) => [c.name, i])
  );

  return (
    <DashboardCardShell
      title={title}
      subtitle={String(campaigns.total)}
      action={action}
      testID="campaigns-card"
    >
      <ScrollView style={styles.list} showsVerticalScrollIndicator={false}>
        {campaigns.items.map((c) => {
          const color = magentaForRank(rankByName.get(c.name) ?? 0);
          return (
            <Pressable
              key={c.name}
              style={styles.row}
              onPress={() => navigate('/campaigns')}
              accessibilityRole="button"
              accessibilityLabel={c.name}
            >
              <View style={styles.rowHeader}>
                <Text style={styles.name} numberOfLines={1}>{c.name}</Text>
                <Text style={[styles.responded, { color }]}>{c.responded}</Text>
              </View>
              <ProgressBar value={c.responded} max={c.sent} color={color} />
            </Pressable>
          );
        })}
      </ScrollView>
    </DashboardCardShell>
  );
};

CampaignsCard.displayName = 'CampaignsCard';

const styles = StyleSheet.create({
  loading: {
    gap: tokens.spacing.sm,
  },
  sampleTag: {
    ...tokens.typography.label.small,
    color: Colors.role.textSoft,
    fontStyle: 'italic',
  },
  list: {
    flex: 1,
  },
  row: {
    gap: tokens.spacing.xs,
    paddingVertical: tokens.spacing.xs,
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.sm,
  },
  name: {
    ...tokens.typography.label.medium,
    fontSize: 13,
    lineHeight: 18,
    color: Colors.role.textStrong,
    flex: 1,
  },
  responded: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
    color: Colors.role.textStrong,
  },
});
